import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon, ArrowUpward, ArrowDownward, Add as AddIcon } from '@mui/icons-material';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const newId = () => Date.now().toString() + Math.floor(Math.random() * 1000);
const emptyBlock = (type = 'paragraph') => ({ id: newId(), type, content: '' });
const emptySection = () => ({ id: newId(), heading: '', blocks: [emptyBlock('paragraph')] });

const AdminPrivacyPolicyClean = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('Customer Terms and Conditions');
  const [sections, setSections] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const ref = doc(db, 'settings', 'privacyPolicy');
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          setLastUpdated(data.lastUpdated || null);
          setTitle(data.title || 'Customer Terms and Conditions');
          if (data.sections && Array.isArray(data.sections)) {
            setSections(data.sections.map((s) => ({ ...s })));
          } else if (typeof data.content === 'string') {
            // legacy plain text -> put into single section
            setSections([{ ...emptySection(), blocks: [{ id: newId(), type: 'paragraph', content: data.content }] }]);
          } else {
            setSections([emptySection()]);
          }
        } else {
          setSections([emptySection()]);
        }
      } catch (err) {
        console.error('Failed to load admin privacy policy', err);
        setMessage({ type: 'error', text: 'Failed to load policy.' });
        setSections([emptySection()]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const ref = doc(db, 'settings', 'privacyPolicy');
      await setDoc(ref, { title, sections, lastUpdated: serverTimestamp() }, { merge: true });
      const snap = await getDoc(ref);
      if (snap.exists()) setLastUpdated(snap.data().lastUpdated || null);
      setMessage({ type: 'success', text: 'Policy saved.' });
    } catch (err) {
      console.error('Failed to save privacy policy', err);
      setMessage({ type: 'error', text: 'Failed to save policy.' });
    } finally {
      setSaving(false);
    }
  };

  const updateSectionField = (sectionId, field, value) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, [field]: value } : s)));
  };

  const addSection = (atIndex = sections.length) => {
    const s = emptySection();
    setSections((prev) => {
      const copy = [...prev];
      copy.splice(atIndex + 1, 0, s);
      return copy;
    });
  };

  const deleteSection = (id) => setSections((prev) => prev.filter((s) => s.id !== id));

  const moveSection = (index, dir) => {
    setSections((prev) => {
      const copy = [...prev];
      const newIndex = index + dir;
      if (newIndex < 0 || newIndex >= copy.length) return copy;
      const [item] = copy.splice(index, 1);
      copy.splice(newIndex, 0, item);
      return copy;
    });
  };

  const addBlock = (sectionId, type = 'paragraph') => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, blocks: [...s.blocks, emptyBlock(type)] } : s)));
  };

  const updateBlockContent = (sectionId, blockId, value) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, blocks: s.blocks.map((b) => (b.id === blockId ? { ...b, content: value } : b)) } : s)));
  };

  const deleteBlock = (sectionId, blockId) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, blocks: s.blocks.filter((b) => b.id !== blockId) } : s)));
  };

  const moveBlock = (sectionId, index, dir) => {
    setSections((prev) => prev.map((s) => {
      if (s.id !== sectionId) return s;
      const copy = [...s.blocks];
      const newIndex = index + dir;
      if (newIndex < 0 || newIndex >= copy.length) return s;
      const [item] = copy.splice(index, 1);
      copy.splice(newIndex, 0, item);
      return { ...s, blocks: copy };
    }));
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ background: 'linear-gradient(90deg,#fff,#fff)', color: '#111', p: 3 }}>
          <Typography variant="h5">Policy Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage all platform policies</Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Edit Policy
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                  {message.text}
                </Alert>
              )}

              <TextField label="Policy Title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {sections.map((s, idx) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>Section {idx + 1}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => moveSection(idx, -1)} aria-label="move up">
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveSection(idx, 1)} aria-label="move down">
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteSection(s.id)} aria-label="delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <TextField label="Heading" fullWidth value={s.heading} onChange={(e) => updateSectionField(s.id, 'heading', e.target.value)} sx={{ mb: 1 }} />

                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {s.blocks.map((b, bi) => (
                        <Paper key={b.id} variant="outlined" sx={{ p: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">{b.type === 'subheading' ? 'Subheading' : 'Paragraph'}</Typography>
                            <Box>
                              <IconButton size="small" onClick={() => moveBlock(s.id, bi, -1)}>
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => moveBlock(s.id, bi, 1)}>
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => deleteBlock(s.id, b.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <TextField
                            multiline
                            minRows={b.type === 'subheading' ? 1 : 3}
                            fullWidth
                            value={b.content}
                            onChange={(e) => updateBlockContent(s.id, b.id, e.target.value)}
                          />
                        </Paper>
                      ))}
                    </Stack>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => addBlock(s.id, 'subheading')}>
                        + Add Subheading
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => addBlock(s.id, 'paragraph')}>
                        + Add Paragraph
                      </Button>
                    </Box>

                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button startIcon={<AddIcon />} onClick={() => addSection(idx)}>
                        Add Section Below
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Stack>

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="contained" color="success" onClick={() => setSections((prev) => [...prev, emptySection()])} startIcon={<AddIcon />}>
                  Add Section
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminPrivacyPolicyClean;
